/* eslint-disable @typescript-eslint/no-explicit-any */
import { masks } from '@/utils/masks';
import {
  Button,
  Input as ChakraInput,
  Select,
  HStack,
  Image,
  ImageProps,
  InputLeftElementProps,
  InputProps,
  StackProps,
  Text,
  Textarea,
  VStack,
  TextareaProps,
} from '@chakra-ui/react';
import { cloneElement, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { FaTimes } from 'react-icons/fa';

export const Input = forwardRef(function (
  {
    name,
    login,
    mask,
    leftElement,
    leftElementProps = {},
    rightElement,
    rightElementProps = {},
    onValueChange,
    control,
    label,
    ...props
  }: InputProps & IInputProps,
  ref
) {
  const animatedTextEffect = props.value || props.onBlur;

  const refFile = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState('email');
  const labelElement = useMemo(
    () => (
      <Text
        transition={'0.2s'}
        fontWeight={500}
        height={'15px'}
        zIndex={9}
        fontFamily={'DM Sans'}
        color={animatedTextEffect ? '#5e5e5b' : '#5e5e5b'}
        top={animatedTextEffect ? '0px' : '4px'}
        left={animatedTextEffect ? '0px' : '5px'}
        fontSize={animatedTextEffect ? '12px' : '12px'}
      >
        {label}
      </Text>
    ),
    [animatedTextEffect, label]
  );

  return (
    <Controller
      name={name}
      defaultValue=""
      control={control}
      render={({ field, fieldState: { error } }) => {
        return (
          <VStack width={'100%'} spacing={0} align={'flex-start'}>
            <HStack
              backgroundColor={'#FAFAF9'}
              width={'100%'}
              padding={'4px 12px 4px 12px'}
              borderRadius={'16px'}
              border={error ? '2px solid #ED4337' : '2px solid #EBEBE6'}
            >
              {leftElement && cloneElement(leftElement, leftElementProps)}
              <VStack width={'100%'} spacing={0} align={'flex-start'}>
                {label && labelElement}
                {login && (
                  <Select
                    color={'#5e5e5b'}
                    fontWeight={500}
                    fontSize={'12px'}
                    size="xs"
                    onChange={(e) => setMode(e.target.value)}
                    width="100%"
                    defaultValue={'email'}
                    variant={'unstyled'}
                  >
                    <option value="email">Email</option>
                    <option value="cpf">CPF/CNPJ</option>
                  </Select>
                )}
                <ChakraInput
                  {...props}
                  {...field}
                  value={props.type === 'file' ? '' : field.value}
                  paddingTop={label ? (animatedTextEffect ? '18px' : '0px') : '0px'}
                  border="0px"
                  variant={'unstyled'}
                  style={{ display: props.type === 'file' ? 'none' : 'block' }}
                  onChange={(event) => {
                    if (props.type === 'file' && event.target?.files && event.target.files.length > 0) {
                      field.onChange(event.target.files[0]);
                      return;
                    }

                    let value = event.target.value;
                    if (mask && !login) {
                      value = masks[mask](value);
                    } else if (login && mode === 'cpf') {
                      value = masks.document(value);
                    }
                    field.onChange(value);
                    if (onValueChange) {
                      onValueChange(value);
                    }
                  }}
                  color={'#292927'}
                  width={'100%'}
                  minH={label ? (animatedTextEffect ? 'inherit' : '32px') : '30px'}
                  ref={refFile}
                  fontSize={'16px'}
                />
                {props.type === 'file' && (
                  <Button
                    onClick={() => {
                      if (refFile && 'current' in refFile && refFile.current) {
                        (refFile.current as HTMLInputElement).click();
                      }
                    }}
                    margin="10px 0px"
                    maxWidth="200px"
                    width={'100%'}
                    display={'flex'}
                    justifyContent={'flex-start'}
                    backgroundColor="#EBEBE6"
                    color="#292927"
                    fontSize="16px"
                    fontWeight="500"
                    borderRadius="16px"
                    _hover={{ backgroundColor: '#DADADA' }}
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                  >
                    {field?.value?.name || 'Escolha um arquivo'}
                  </Button>
                )}
              </VStack>
              {rightElement && cloneElement(rightElement, { fontSize: '26px', color: 'black', ...rightElementProps })}
            </HStack>
            {error?.message && (
              <Text
                color={'#ED4337'}
                fontSize={'12px'}
                fontWeight={500}
                fontFamily={'DM Sans'}
                width={'100%'}
                textAlign={'left'}
              >
                {error?.message}
              </Text>
            )}
          </VStack>
        );
      }}
    />
  );
});

export const InputArea = forwardRef(function (
  {
    name,
    mask,
    leftElement,
    leftElementProps = {},
    rightElement,
    rightElementProps = {},
    onValueChange,
    control,
    label = '',
    ...props
  }: TextareaProps & IInputProps,
  ref
) {
  const animatedTextEffect = props.value || props.onBlur;
  const labelElement = useMemo(
    () => (
      <Text
        transition={'0.2s'}
        fontWeight={500}
        height={'15px'}
        zIndex={9}
        fontFamily={'DM Sans'}
        color={animatedTextEffect ? '#5e5e5b' : '#5e5e5b'}
        top={animatedTextEffect ? '0px' : '4px'}
        left={animatedTextEffect ? '0px' : '5px'}
        fontSize={animatedTextEffect ? '12px' : '12px'}
      >
        {label}
      </Text>
    ),
    [animatedTextEffect, label]
  );

  return (
    <Controller
      name={name}
      defaultValue=""
      control={control}
      render={({ field, fieldState: { error } }) => (
        <VStack width={'100%'} spacing={0} align={'flex-start'}>
          <HStack
            backgroundColor={'#FAFAF9'}
            width={'100%'}
            padding={'4px 12px 4px 12px'}
            borderRadius={'16px'}
            border={error ? '2px solid #ED4337' : '2px solid #EBEBE6'}
          >
            {leftElement && cloneElement(leftElement, leftElementProps)}
            <VStack width={'100%'} spacing={0} align={'flex-start'}>
              {labelElement}
              <Textarea
                {...props}
                {...field}
                paddingTop={label ? (animatedTextEffect ? '18px' : '0px') : '0px'}
                border="0px"
                variant={'unstyled'}
                style={{ display: props.type === 'file' ? 'none' : 'block' }}
                onChange={(event) => {
                  let value = event.target.value;
                  if (mask) {
                    value = masks[mask](value);
                  }
                  field.onChange(value);
                  if (onValueChange) {
                    onValueChange(value);
                  }
                }}
                color={'#292927'}
                width={'100%'}
                minH={label ? (animatedTextEffect ? 'inherit' : '32px') : '30px'}
                ref={ref}
                fontSize={'16px'}
              />
            </VStack>

            {rightElement && cloneElement(rightElement, { fontSize: '26px', color: 'black', ...rightElementProps })}
          </HStack>
          {error?.message && (
            <Text
              color={'#ED4337'}
              fontSize={'12px'}
              fontWeight={500}
              fontFamily={'DM Sans'}
              width={'100%'}
              textAlign={'left'}
            >
              {error?.message}
            </Text>
          )}
        </VStack>
      )}
    />
  );
});

export const SelectInput = forwardRef(function (
  { name, control, label = '', placeholder, options, ...props }: any & ISelectProps,
  ref
) {
  const animatedTextEffect = props.value || props.onBlur;
  const labelElement = useMemo(
    () => (
      <Text
        transition={'0.2s'}
        fontWeight={500}
        height={'15px'}
        zIndex={9}
        fontFamily={'DM Sans'}
        color={animatedTextEffect ? '#5e5e5b' : '#5e5e5b'}
        top={animatedTextEffect ? '0px' : '4px'}
        left={animatedTextEffect ? '0px' : '5px'}
        fontSize={animatedTextEffect ? '12px' : '12px'}
      >
        {label}
      </Text>
    ),
    [animatedTextEffect, label]
  );

  return (
    <Controller
      name={name}
      defaultValue=""
      control={control}
      render={({ field, fieldState: { error } }) => (
        <HStack
          backgroundColor={'#FAFAF9'}
          width={'100%'}
          padding={'4px 12px 4px 12px'}
          borderRadius={'16px'}
          border={error ? '2px solid #ED4337' : '2px solid #EBEBE6'}
        >
          <VStack width={'100%'} spacing={0} align={'flex-start'}>
            {labelElement}
            <Select
              {...field}
              paddingTop={label ? (animatedTextEffect ? '18px' : '0px') : '0px'}
              border="0px"
              variant={'unstyled'}
              color={'#292927'}
              width={'100%'}
              placeholder={placeholder}
              minH={label ? (animatedTextEffect ? 'inherit' : '32px') : '30px'}
              fontSize={'16px'}
            >
              {options.map((option: { value: string; label: string }) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </VStack>
        </HStack>
      )}
    />
  );
});

export function ImageInput({
  value,
  onImageChange,
  imageProps,
  ...props
}: StackProps & {
  value: File | undefined;
  imageProps?: ImageProps;
  onImageChange: (value: File | undefined) => void;
}) {
  const [imageUrl, setImageUrl] = useState<string>();

  function onClick() {
    const input: HTMLInputElement = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.position = 'fixed';
    input.style.zIndex = '-1';

    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;

      if (!target || !target.files) return;

      const image = target.files[0];

      onImageChange(image);
    };

    document.body.appendChild(input);
    input.click();
  }

  useEffect(() => {
    if (!value) {
      setImageUrl(undefined);
      return;
    }

    const reader = new FileReader();

    reader.onloadend = function () {
      setImageUrl(reader.result as string);
    };

    reader.readAsDataURL(value);
  }, [value]);

  return (
    <VStack {...props} width="100%" position="relative">
      {value && (
        <FaTimes
          style={{
            cursor: 'pointer',
            position: 'absolute',
            right: '0px',
            top: '0px',
          }}
          onClick={() => onImageChange(undefined)}
        />
      )}
      {imageUrl && <Image {...imageProps} src={imageUrl} />}
      <Button width="100%" colorScheme="blue" onClick={onClick}>
        {value ? 'Trocar' : 'Adicionar'}
      </Button>
    </VStack>
  );
}

interface IInputProps {
  control: any;
  login?: boolean;
  leftElement?: JSX.Element;
  leftElementProps?: StackProps;
  rightElement?: JSX.Element;
  label?: string;
  placeholder?: string;
  rightElementProps?: InputLeftElementProps;
  onValueChange?: (value: string) => void;
  onInputChange?: (value: string) => void;
  mask?: string;
  maskChar?: string | null;
  disabled?: boolean;
  name: string;
  type?: string;
}

interface ISelectProps {
  control: any;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  name: string;
  options: { value: string; label: string }[];
}
